import {
  Grid,
  IconButton,
  TextField,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  Typography,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from "@emotion/react";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import { BiTrash, BiEdit } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Supplier,
  setSearchBody,
} from "../../../../../app/slices/supplierSlice";
import dayjs from "dayjs";
import ConfirmationDialog from "../../../../../helpers/ConfirmationDialog";
import { AiOutlineTransaction } from "react-icons/ai";
import InitialSupplierBalanceForm from "../../form/InitialBalance";
import Permission from "../../../../../components/userPermission/userPermission";
import { HiOutlineTruck, HiOutlineBanknotes } from "react-icons/hi2";
import {
  FILTERS_SX,
  MobileAction,
  MobileEmpty,
  MobileListItem,
  MobileListShell,
  MobilePagination,
  MobileSummaryCard,
  TONE,
} from "../../../../../components/MobileList";
import {
  EditableField,
  useInlineEditConfig,
} from "../../../../../components/MobileList/inlineEdit";
import supplierInlineEdit from "./inlineEdit";

const SupplierTabel = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);
  const dispatch = useDispatch();

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const _Supplier = useSelector((state) => state.supplier.suppliers);
  const loading = useSelector((state) => state.supplier.supplier.loading);
  const _SearchBody = useSelector((state) => state.supplier.searchBody);
  const _InitialSupplierBalance = useSelector(
    (state) => state.supplier.initialBalance.dialog,
  );

  const [openConfirm, setConfirmOpen] = useState(false);
  const [dialogData, setDialogData] = useState("");

  const handleDeleteClick = (params) => {
    setConfirmOpen(true);
    setDialogData(params);
  };

  const handleConfirmDelete = () => {
    dispatch(Supplier.deleteById(dialogData.id, _SearchBody));
    setConfirmOpen(false);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
  };

  const handlePageChange = (newPage) => {
    dispatch(
      Supplier.getAll({
        search: search,
        skip: newPage * page,
        take: page,
      }),
    );
    setCurrentPage(newPage);
  };

  // This table pages from local state rather than the slice's searchBody, so
  // the refresh has to rebuild the same params handlePageChange uses —
  // refetching with _SearchBody would silently drop the user back to page 1.
  const inlineEdit = useInlineEditConfig(
    supplierInlineEdit,
    () =>
      dispatch(
        Supplier.getAll({
          search: search,
          skip: currentPage * page,
          take: page,
        }),
      ),
    [dispatch, search, currentPage, page],
  );

  const handlePageSizeChange = (newPageSize) => {
    dispatch(
      Supplier.getAll({
        search: search,
        skip: 0,
        take: newPageSize,
      }),
    );
    setPage(newPageSize);
    setCurrentPage(0);
  };

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.2,
    },
    {
      field: "name",
      headerName: "الاسم",
      flex: 1,
    },
    {
      field: "province_name",
      headerName: "المحافظة",
      flex: 0.6,
    },
    {
      field: "credit",
      headerName: "الرصيد",
      flex: 1,
      renderCell: (params) => {
        return <>{parseInt(params.row.balance).toLocaleString()}</>;
      },
    },
    {
      field: "created_at",
      headerName: "التاريخ",
      flex: 1,
      renderCell: (params) => {
        return (
          <Stack spacing={0.5}>
            <Typography variant="body">
              {dayjs(params.row.created_at).format("YYYY-MM-DD")}
            </Typography>
            <Typography variant="body">
              {dayjs(params.row.created_at).format("hh:mm:ss a")}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "operations",
      headerName: "العمليات",
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <Permission permission="supplier_transactions_view">
              <Tooltip title="المناقلات">
                <IconButton
                  onClick={() =>
                    navigate(
                      "/app/suppliers&bills/suppliers/transactions/" +
                        params.row.id,
                    )
                  }
                >
                  <AiOutlineTransaction />
                </IconButton>
              </Tooltip>
            </Permission>
            {/* <Tooltip title="الرصيد">
              <IconButton
                variant="contained"
                color="info"
                onClick={() => dispatch(setInitialBalanceDialog(params.row.id))}
              >
                <TbTransferIn />
              </IconButton>
            </Tooltip> */}
            <Tooltip title="تعديل">
              <IconButton
                onClick={() => dispatch(Supplier.getById(params.row.id))}
              >
                <BiEdit />
              </IconButton>
            </Tooltip>
            <Tooltip title="حذف">
              <IconButton onClick={() => handleDeleteClick(params.row)}>
                <BiTrash />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(Supplier.getAll({ search: search, skip: 0, take: page }));
      dispatch(setSearchBody({ search: search, skip: 0, take: page }));
      setCurrentPage(0);
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const navigate = useNavigate();

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          المجموع الكلي : {_Supplier.total_amount.toLocaleString()} (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box>
          {_InitialSupplierBalance && <InitialSupplierBalanceForm />}
          <ConfirmationDialog
            open={openConfirm}
            title="تاكيد"
            message="هل انت متاكد من حذف"
            name={dialogData.name}
            confirmText="حذف"
            cancelText="الغاء"
            onConfirm={() => handleConfirmDelete()}
            onCancel={handleCancelDelete}
          />
        </Box>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={2}
          sx={smallScreen ? FILTERS_SX : undefined}
        >
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              label="بحث عن الاسم "
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" component="div">
              * اذا كان رصيد المورد اكبر من صفر معناه ان الشركة مديونة للمورد
              واذا كان رصيد المورد اصغر من صفر معناه ان المورد مطلوب للشركة
            </Typography>
          </Grid>
          {smallScreen ? (
            <Grid item xs={12}>
              <MobileListShell loading={loading}>
                <MobileSummaryCard
                  value={(_Supplier.total_amount ?? 0).toLocaleString()}
                  icon={<HiOutlineBanknotes />}
                />
                {_Supplier.data?.length > 0 ? (
                  <>
                    {_Supplier.data.map((supplier, index) => (
                      <MobileListItem
                        // Keyed by id, not index: after a refetch React would
                        // otherwise reuse the card at this position for a
                        // different supplier and an open editor would end up
                        // over the wrong record.
                        key={supplier.id ?? index}
                        avatar={<HiOutlineTruck />}
                        title={
                          <EditableField
                            config={inlineEdit}
                            field="name"
                            row={supplier}
                            display={supplier?.name}
                            variant="title"
                          />
                        }
                        subtitle={dayjs(supplier.created_at).format(
                          "YYYY-MM-DD · hh:mm a",
                        )}
                        highlight={{
                          label: "الرصيد",
                          value: parseInt(
                            supplier?.balance ?? 0,
                          ).toLocaleString(),
                          rawValue: supplier?.balance,
                          unit: "IQD",
                          tone: "auto",
                        }}
                        fields={[
                          {
                            label: "المحافظة",
                            value: (
                              <EditableField
                                config={inlineEdit}
                                field="province_name"
                                row={supplier}
                                display={supplier?.province_name}
                              />
                            ),
                            span: 12,
                          },
                        ]}
                        actions={
                          <>
                            <Permission permission="supplier_transactions_view">
                              <MobileAction
                                title="المناقلات"
                                icon={<AiOutlineTransaction />}
                                onClick={() =>
                                  navigate(
                                    "/app/suppliers&bills/suppliers/transactions/" +
                                      supplier.id,
                                  )
                                }
                              />
                            </Permission>
                            <MobileAction
                              title="تعديل"
                              tone={TONE.warning}
                              icon={<BiEdit />}
                              onClick={() =>
                                dispatch(Supplier.getById(supplier.id))
                              }
                            />
                            <MobileAction
                              title="حذف"
                              tone={TONE.danger}
                              icon={<BiTrash />}
                              onClick={() => handleDeleteClick(supplier)}
                            />
                          </>
                        }
                      />
                    ))}
                    <MobilePagination
                      count={_Supplier.total ?? 0}
                      rowsPerPage={page}
                      page={currentPage}
                      onPageChange={(e, newPage) => handlePageChange(newPage)}
                      rowsPerPageOptions={[15, 25, 50, 100]}
                      onRowsPerPageChange={(event) => {
                        handlePageSizeChange(parseInt(event.target.value, 10));
                      }}
                    />
                  </>
                ) : (
                  <MobileEmpty />
                )}
              </MobileListShell>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Box sx={{ height: "70vh", width: "100%" }}>
                <DataGrid
                  rows={
                    _Supplier.data?.map((x, index) => ({
                      ...x,
                      index: index + 1,
                    })) ?? []
                  }
                  columns={columns}
                  pageSize={page}
                  page={currentPage}
                  rowCount={_Supplier.total}
                  loading={loading}
                  rowHeight={50}
                  pagination
                  paginationMode="server"
                  selectionModel={[]}
                  onPageChange={(newPage) => handlePageChange(newPage)}
                  rowsPerPageOptions={[15, 25, 50, 100]}
                  onPageSizeChange={(newPageSize) =>
                    handlePageSizeChange(newPageSize)
                  }
                  components={{
                    LoadingOverlay: LinearProgress,
                    Footer: CustomFooter,
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SupplierTabel;
